import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchHistoryStore } from '../stores/movieStore';

interface SearchBarProps {
  initialQuery?: string;
  onSearch?: (query: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
}

const SearchBar = ({
  initialQuery = '',
  onSearch,
  placeholder = '搜索电影、电视剧、动漫...',
  size = 'md',
}: SearchBarProps) => {
  const [query, setQuery] = useState(initialQuery);
  const [isFocused, setIsFocused] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { queries: history, addQuery, removeQuery, clearQueries } = useSearchHistoryStore();

  useEffect(() => {
    setQuery(initialQuery);
  }, [initialQuery]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowHistory(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      addQuery(query.trim());
      setShowHistory(false);
      if (onSearch) {
        onSearch(query.trim());
      } else {
        navigate(`/search?q=${encodeURIComponent(query.trim())}`);
      }
    }
  };

  const handleHistoryClick = (q: string) => {
    setQuery(q);
    setShowHistory(false);
    addQuery(q);
    if (onSearch) {
      onSearch(q);
    } else {
      navigate(`/search?q=${encodeURIComponent(q)}`);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (history.length > 0) {
      setShowHistory(true);
    }
  };

  const sizeClasses = {
    sm: 'py-2 px-4 text-sm',
    md: 'py-3 px-5 text-base',
    lg: 'py-4 px-6 text-lg',
  };

  return (
    <div ref={containerRef} className="w-full max-w-3xl mx-auto relative">
      <form onSubmit={handleSubmit}>
        <div
          className={`relative group transition-all duration-300 ${
            isFocused ? 'transform scale-[1.02]' : ''
          }`}
        >
          <div
            className={`absolute -inset-1 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 rounded-2xl opacity-0 blur transition-opacity duration-300 ${
              isFocused ? 'opacity-75' : 'group-hover:opacity-50'
            }`}
          />

          <div className="relative flex items-center glass rounded-2xl overflow-hidden">
            <div className="pl-5 text-gray-400">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={handleFocus}
              onBlur={() => setIsFocused(false)}
              placeholder={placeholder}
              className={`flex-1 bg-transparent border-none outline-none text-white placeholder-gray-400 ${sizeClasses[size]}`}
            />

            {query && (
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}

            <button
              type="submit"
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-medium hover:from-blue-600 hover:to-purple-700 transition-all"
            >
              搜索
            </button>
          </div>
        </div>
      </form>

      {showHistory && history.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 glass rounded-xl overflow-hidden z-50 shadow-xl">
          <div className="flex items-center justify-between px-4 py-2 border-b border-slate-700">
            <span className="text-sm text-gray-400">搜索历史</span>
            <button
              onClick={() => {
                clearQueries();
                setShowHistory(false);
              }}
              className="text-xs text-gray-500 hover:text-red-400 transition-colors"
            >
              清空
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {history.map((q, index) => (
              <div
                key={`${q}-${index}`}
                className="flex items-center justify-between px-4 py-2 hover:bg-slate-700/50 cursor-pointer group"
                onClick={() => handleHistoryClick(q)}
              >
                <div className="flex items-center space-x-3">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-gray-300 group-hover:text-white transition-colors">{q}</span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeQuery(q);
                  }}
                  className="text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
